import { get, post, put, del } from '../apiClient';
import { API_ENDPOINTS, API_BASE_URL } from '../config';

const trainingService = {
  // Get all courses with advanced filtering and search
  getCourses: async (queryParams = {}) => {
    try {
      // Build query string from parameters
      const queryString = new URLSearchParams();

      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.type && queryParams.type !== 'all') queryString.append('type', queryParams.type);
      if (queryParams.difficulty && queryParams.difficulty !== 'all') queryString.append('difficulty', queryParams.difficulty);
      if (queryParams.duration && queryParams.duration !== 'all') queryString.append('duration', queryParams.duration);
      if (queryParams.compliance && queryParams.compliance !== 'all') queryString.append('compliance', queryParams.compliance);
      if (queryParams.status && queryParams.status !== 'all') queryString.append('status', queryParams.status);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);
      if (queryParams.contentType) queryString.append('contentType', queryParams.contentType);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);
      if (queryParams.startDate) queryString.append('startDate', queryParams.startDate);
      if (queryParams.endDate) queryString.append('endDate', queryParams.endDate);

      const url = queryString.toString() ? `${API_BASE_URL}/training/courses?${queryString.toString()}` : `${API_BASE_URL}/training/courses`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: {
            courses: response.data.courses || [],
            pagination: response.data.pagination || {}
          },
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch courses'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch courses'
      };
    }
  },

  // Get all courses
  getAllCourses: async () => {
    try {
      const response = await get(`${API_BASE_URL}/training/courses`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch courses'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch courses'
      };
    }
  },

  // Get courses for dropdown (optimized - minimal fields only)
  getCoursesForDropdown: async (options = {}) => {
    try {
      const { status, limit = 1000 } = options;
      const queryString = new URLSearchParams();
      if (status) queryString.append('status', status);
      queryString.append('limit', limit);

      const url = `${API_BASE_URL}/training/courses/dropdown?${queryString.toString()}`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: {
            courses: response.data?.courses || []
          },
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch courses'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch courses'
      };
    }
  },

  // Get course by ID
  getCourseById: async (courseId) => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.COURSES}/${courseId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch course'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch course'
      };
    }
  },

  // Create new course
  createCourse: async (courseData) => {
    try {
      const response = await post(API_ENDPOINTS.TRAINING.COURSES, courseData);

      if (response.responseCode === 201) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create course'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create course'
      };
    }
  },

  // Update course
  updateCourse: async (courseId, courseData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.COURSES}/${courseId}`, courseData);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update course'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update course'
      };
    }
  },

  // Delete course
  deleteCourse: async (courseId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.COURSES}/${courseId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete course'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete course'
      };
    }
  },

  // Publish course
  publishCourse: async (courseId) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.COURSES}/${courseId}/publish`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to publish course'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to publish course'
      };
    }
  },

  // Unpublish course
  unpublishCourse: async (courseId, force = false) => {
    try {
      const url = force 
        ? `${API_ENDPOINTS.TRAINING.COURSES}/${courseId}/unpublish?force=true`
        : `${API_ENDPOINTS.TRAINING.COURSES}/${courseId}/unpublish`;
      const response = await put(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to unpublish course'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to unpublish course',
        isConflict: error.response && error.response.status === 409,
        responseCode: error.response ? error.response.status : undefined
      };
    }
  },

  // Archive course
  archiveCourse: async (courseId) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.COURSES}/${courseId}/archive`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to archive course'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to archive course'
      };
    }
  },

  // Unarchive course
  unarchiveCourse: async (courseId) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.COURSES}/${courseId}/unarchive`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to unarchive course'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to unarchive course'
      };
    }
  },

  // Enroll in course
  enrollInCourse: async (courseId, employeeId) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.COURSES}/${courseId}/enroll`, { employeeId });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to enroll in course'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to enroll in course'
      };
    }
  },

  // Get course participants
  getCourseParticipants: async (courseId) => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.COURSES}/${courseId}/participants`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch course participants'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch course participants'
      };
    }
  },

  // Get training sessions
  getSessions: async (queryParams = {}) => {
    try {
      // Build query string from parameters
      const queryString = new URLSearchParams();

      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.mode && queryParams.mode !== 'all') queryString.append('mode', queryParams.mode);
      if (queryParams.status && queryParams.status !== 'all') queryString.append('status', queryParams.status);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);
      if (queryParams.startDate) queryString.append('startDate', queryParams.startDate);
      if (queryParams.endDate) queryString.append('endDate', queryParams.endDate);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);

      const url = queryString.toString() ? `${API_BASE_URL}/training-sessions?${queryString.toString()}` : `${API_BASE_URL}/training-sessions`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch sessions'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch sessions'
      };
    }
  },

  // Create training session
  createSession: async (sessionData) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();

      // Add all session data fields

      Object.keys(sessionData).forEach(key => {
        if (key === 'materials') {
          // Handle materials separately
          return;
        } else if ((key === 'trainerIds' || key === 'invitedParticipantIds') && Array.isArray(sessionData[key])) {
          // Convert array to JSON string
          const stringified = JSON.stringify(sessionData[key]);
          formData.append(key, stringified);
        } else if (key === 'invitedParticipantIds' && !Array.isArray(sessionData[key])) {
          // Handle case where it's not an array - convert to array first
          const arrayValue = Array.isArray(sessionData[key]) ? sessionData[key] : (sessionData[key] ? [sessionData[key]] : []);
          formData.append(key, JSON.stringify(arrayValue));
        } else {
          formData.append(key, sessionData[key]);
        }
      });



      // Separate new files from existing materials
      const newFiles = [];
      const existingMaterials = [];

      if (sessionData.materials && sessionData.materials.length > 0) {
        sessionData.materials.forEach((material) => {
          if (material.file) {
            newFiles.push(material);
          } else if (material.url) {
            existingMaterials.push({
              name: material.name,
              type: material.type,
              url: material.url,
              size: material.size,
              publicId: material.publicId || null,
              format: material.format || null,
              resourceType: material.resourceType || 'raw'
            });
          }
        });
      }

      // Add existing materials as JSON string
      if (existingMaterials.length > 0) {
        formData.append('existingMaterials', JSON.stringify(existingMaterials));
      }

      // Add files to FormData
      newFiles.forEach((material) => {
        if (material.file) {
          formData.append('materials', material.file);
        }
      });

      const response = await fetch(`${API_BASE_URL}/training-sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          data: result.data,
          message: result.message
        };
      } else {
        return {
          success: false,
          error: result.message || 'Failed to create session'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create session'
      };
    }
  },

  // Update training session
  updateSession: async (sessionId, sessionData) => {
    try {
      // Always use FormData so the backend (multer) receives consistent payloads
      const formData = new FormData();

      // Separate new files from existing materials
      const newFiles = [];
      const existingMaterials = [];

      if (sessionData.materials && sessionData.materials.length > 0) {
        sessionData.materials.forEach((material) => {
          if (material.file) {
            // New file to upload
            newFiles.push(material);
          } else if (material.url) {
            // Existing material (already has URL)
            existingMaterials.push({
              name: material.name,
              type: material.type,
              url: material.url,
              size: material.size,
              publicId: material.publicId || null,
              format: material.format || null,
              resourceType: material.resourceType || 'raw'
            });
          }
        });
      }


      // Always send existing materials, even if empty, so backend can clear removed files
      formData.append('existingMaterials', JSON.stringify(existingMaterials));

      Object.keys(sessionData).forEach(key => {
        if (key === 'materials') {
          return; // already handled above
        } else if ((key === 'trainerIds' || key === 'invitedParticipantIds') && Array.isArray(sessionData[key])) {
          // Convert array to JSON string
          const stringified = JSON.stringify(sessionData[key]);
          formData.append(key, stringified);
        } else if (key === 'invitedParticipantIds' && !Array.isArray(sessionData[key])) {
          // Handle case where it's not an array - convert to array first
          const arrayValue = Array.isArray(sessionData[key]) ? sessionData[key] : (sessionData[key] ? [sessionData[key]] : []);
          formData.append(key, JSON.stringify(arrayValue));
        } else if (sessionData[key] !== undefined && sessionData[key] !== null) {
          formData.append(key, sessionData[key]);
        }
      });

      // Debug: Log what's in FormData

      // Add new files to FormData (multer expects field name 'materials')
      newFiles.forEach((material) => {
        if (material.file) {
          formData.append('materials', material.file);
        }
      });

      const response = await fetch(`${API_BASE_URL}/training-sessions/${sessionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formData
      });

      const data = await response.json();

      if (data.responseCode === 200) {
        return {
          success: true,
          data: data.data,
          message: data.responseMessage
        };
      } else {
        return {
          success: false,
          error: data.responseMessage || 'Failed to update session'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update session'
      };
    }
  },

  // Delete training session
  deleteSession: async (sessionId) => {
    try {
      const response = await del(`${API_BASE_URL}/training-sessions/${sessionId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete session'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete session'
      };
    }
  },

  // Get session enrollments and attendance
  getSessionEnrollments: async (sessionId) => {
    try {
      const response = await get(`${API_BASE_URL}/training-sessions/${sessionId}/enrollments`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch session enrollments'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch session enrollments'
      };
    }
  },

  // Get session by ID
  getSessionById: async (sessionId) => {
    try {
      const response = await get(`${API_BASE_URL}/training-sessions/${sessionId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch session'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch session'
      };
    }
  },

  // Publish session
  publishSession: async (sessionId) => {
    try {
      const response = await put(`${API_BASE_URL}/training-sessions/${sessionId}/publish`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to publish session'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to publish session'
      };
    }
  },

  // Cancel session
  cancelSession: async (sessionId) => {
    try {
      const response = await put(`${API_BASE_URL}/training-sessions/${sessionId}/cancel`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to cancel session'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to cancel session'
      };
    }
  },

  // Delete session
  deleteSession: async (sessionId) => {
    try {
      const response = await del(`${API_BASE_URL}/training-sessions/${sessionId}`);
      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to delete session' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to delete session' };
    }
  },

  // Reschedule session
  rescheduleSession: async (sessionId, rescheduleData) => {
    try {
      const response = await put(`${API_BASE_URL}/training-sessions/${sessionId}/reschedule`, rescheduleData);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to reschedule session'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to reschedule session'
      };
    }
  },

  // Get training assignments
  getAssignments: async (queryParams = {}) => {
    try {
      // Build query string from parameters
      const queryString = new URLSearchParams();

      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.status && queryParams.status !== 'all' && queryParams.status !== '') queryString.append('status', queryParams.status);
      if (queryParams.courseId && queryParams.courseId !== 'all') queryString.append('courseId', queryParams.courseId);
      if (queryParams.quizId && queryParams.quizId !== 'all') queryString.append('quizId', queryParams.quizId);
      if (queryParams.assigneeId && queryParams.assigneeId !== 'all') queryString.append('assigneeId', queryParams.assigneeId);
      if (queryParams.employeeId && queryParams.employeeId !== 'all') queryString.append('employeeId', queryParams.employeeId);
      if (queryParams.assigneeType && queryParams.assigneeType !== 'all' && queryParams.assigneeType !== '') queryString.append('assigneeType', queryParams.assigneeType);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);
      if (queryParams.contentType) queryString.append('contentType', queryParams.contentType);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.ASSIGNMENTS}?${queryString.toString()}` : API_ENDPOINTS.TRAINING.ASSIGNMENTS;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch assignments'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch assignments'
      };
    }
  },

  // Get assignment by ID
  getAssignmentById: async (assignmentId) => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.ASSIGNMENTS}/${assignmentId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch assignment'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch assignment'
      };
    }
  },

  // Bulk upload assignments
  bulkUploadAssignments: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_ENDPOINTS.TRAINING.ASSIGNMENTS}/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        return {
          success: true,
          data: result.data,
          message: result.responseMessage
        };
      } else {
        return {
          success: false,
          error: result.responseMessage || 'Failed to upload file'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to upload file'
      };
    }
  },

  // Create assignment
  createAssignment: async (assignmentData) => {
    try {
      const response = await post(API_ENDPOINTS.TRAINING.ASSIGNMENTS, assignmentData);

      if (response.responseCode === 200 || response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create assignment'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create assignment'
      };
    }
  },

  // Update assignment
  updateAssignment: async (assignmentId, assignmentData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.ASSIGNMENTS}/${assignmentId}`, assignmentData);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update assignment'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update assignment'
      };
    }
  },

  // Delete assignment
  deleteAssignment: async (assignmentId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.ASSIGNMENTS}/${assignmentId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete assignment'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete assignment'
      };
    }
  },

  // Get single assignment
  getAssignment: async (assignmentId) => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.ASSIGNMENTS}/${assignmentId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch assignment'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch assignment'
      };
    }
  },

  // Download bulk assignment template
  downloadBulkTemplate: async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.TRAINING.ASSIGNMENTS}/bulk-template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'bulk_assignment_template.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: true };
      } else {
        return {
          success: false,
          error: 'Failed to download template'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to download template'
      };
    }
  },

  // Get certificates
  getCertificates: async (queryParams = {}) => {
    try {
      // Build query string from parameters
      const queryString = new URLSearchParams();

      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.status && queryParams.status !== 'all') queryString.append('status', queryParams.status);
      if (queryParams.courseId && queryParams.courseId !== 'all') queryString.append('courseId', queryParams.courseId);
      if (queryParams.templateId && queryParams.templateId !== 'all') queryString.append('templateId', queryParams.templateId);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.CERTIFICATES}?${queryString.toString()}` : API_ENDPOINTS.TRAINING.CERTIFICATES;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch certificates'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch certificates'
      };
    }
  },

  // Generate certificate
  generateCertificate: async (certificateData) => {
    try {
      const response = await post(API_ENDPOINTS.TRAINING.CERTIFICATES, certificateData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to generate certificate'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to generate certificate'
      };
    }
  },

  // Get certificate by ID
  getCertificateById: async (certificateId) => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.CERTIFICATES}/${certificateId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch certificate'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch certificate'
      };
    }
  },

  // Download certificate
  downloadCertificate: async (certificateId) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.CERTIFICATES}/${certificateId}/download`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to download certificate'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to download certificate'
      };
    }
  },

  // Get training analytics
  getAnalytics: async (period = '30d') => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.ANALYTICS}?period=${period}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch analytics'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch analytics'
      };
    }
  },

  // Get training reports
  getReports: async (type = 'completion', format = 'json') => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.REPORTS}?type=${type}&format=${format}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch reports'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch reports'
      };
    }
  },

  // Get training progress
  getProgress: async (employeeId = '', courseId = '') => {
    try {
      let queryString = '';
      if (employeeId) queryString += `employeeId=${employeeId}`;
      if (courseId) queryString += queryString ? `&courseId=${courseId}` : `courseId=${courseId}`;

      const url = queryString ? `${API_ENDPOINTS.TRAINING.PROGRESS}?${queryString}` : API_ENDPOINTS.TRAINING.PROGRESS;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch progress'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch progress'
      };
    }
  },

  // ===== MODULES & LESSONS APIs =====

  // Get modules
  getModules: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.TRAINING.MODULES}?${queryString}` : API_ENDPOINTS.TRAINING.MODULES;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data.modules || response.data,
          pagination: response.data.pagination,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch modules'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch modules'
      };
    }
  },

  // Create module
  createModule: async (moduleData) => {
    try {
      const response = await post(API_ENDPOINTS.TRAINING.MODULES, moduleData);

      if (response.responseCode === 201) {
        return {
          success: true,
          responseCode: 201,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to create module'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create module'
      };
    }
  },

  // Update module
  updateModule: async (moduleId, moduleData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}`, moduleData);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to update module'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update module'
      };
    }
  },

  // Delete module
  deleteModule: async (moduleId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to delete module'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete module'
      };
    }
  },

  // Get lessons for module
  getLessons: async (moduleId) => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}/lessons`);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch lessons'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch lessons'
      };
    }
  },

  // Get lessons for a specific module in course structure
  getModuleLessons: async (courseId, moduleId) => {
    try {
      // Since the backend doesn't have a proper lessons endpoint that returns real data,
      // we'll use the course structure endpoint and extract lessons from the specific module
      const response = await get(API_ENDPOINTS.TRAINING.COURSE_STRUCTURE(courseId));

      if (response.responseCode === 200) {
        const courseStructure = response.data;
        const modules = courseStructure.modules || [];

        // Find the specific module and get its lessons
        const targetModule = modules.find(module => module.id === moduleId);
        const lessons = targetModule?.lessons || [];


        return {
          success: true,
          responseCode: 200,
          data: lessons,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch module lessons'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch module lessons'
      };
    }
  },

  // Create lesson
  createLesson: async (moduleId, lessonData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}/lessons`, lessonData);

      if (response.responseCode === 201) {
        return {
          success: true,
          responseCode: 201,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to create lesson'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create lesson'
      };
    }
  },

  // ===== QUIZ & QUESTION BANK APIs =====

  // Get questions
  getQuestions: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.TRAINING.QUIZZES}/questions?${queryString}` : `${API_ENDPOINTS.TRAINING.QUIZZES}/questions`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data.questions || response.data,
          pagination: response.data.pagination,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch questions'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch questions'
      };
    }
  },

  // Create question
  createQuestion: async (questionData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.QUIZZES}/questions`, questionData);

      if (response.responseCode === 201) {
        return {
          success: true,
          responseCode: 201,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to create question'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create question'
      };
    }
  },

  // Update question
  updateQuestion: async (questionId, questionData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.QUIZZES}/questions/${questionId}`, questionData);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to update question'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update question'
      };
    }
  },

  // Delete question
  deleteQuestion: async (questionId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.QUIZZES}/questions/${questionId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to delete question'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete question'
      };
    }
  },

  // Get quizzes
  getQuizzes: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.TRAINING.QUIZZES}/quizzes?${queryString}` : `${API_ENDPOINTS.TRAINING.QUIZZES}/quizzes`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data.quizzes || response.data,
          pagination: response.data.pagination,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch quizzes'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch quizzes'
      };
    }
  },

  // Create quiz
  createQuiz: async (quizData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.QUIZZES}/quizzes`, quizData);

      if (response.responseCode === 201) {
        return {
          success: true,
          responseCode: 201,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to create quiz'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create quiz'
      };
    }
  },

  // Update quiz
  updateQuiz: async (quizId, quizData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.QUIZZES}/quizzes/${quizId}`, quizData);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to update quiz'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update quiz'
      };
    }
  },

  // Delete quiz
  deleteQuiz: async (quizId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.QUIZZES}/quizzes/${quizId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to delete quiz'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete quiz'
      };
    }
  },

  // ===== COURSE STRUCTURE APIs =====

  // Get course structure (modules and lessons)
  getCourseStructure: async (courseId) => {
    try {
      const url = API_ENDPOINTS.TRAINING.COURSE_STRUCTURE(courseId);
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch course structure'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch course structure'
      };
    }
  },

  // Add module to course structure
  addModuleToCourse: async (courseId, moduleData) => {
    try {
      const url = API_ENDPOINTS.TRAINING.COURSE_MODULES(courseId);
      const response = await post(url, moduleData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to add module'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to add module'
      };
    }
  },

  // Update module in course structure
  updateModuleInCourse: async (courseId, moduleId, moduleData) => {
    try {
      const response = await put(API_ENDPOINTS.TRAINING.COURSE_MODULE(courseId, moduleId), moduleData);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update module'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update module'
      };
    }
  },

  // Delete module from course structure
  deleteModuleFromCourse: async (courseId, moduleId) => {
    try {
      const response = await del(API_ENDPOINTS.TRAINING.COURSE_MODULE(courseId, moduleId));

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete module'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete module'
      };
    }
  },

  // Add lesson to module
  addLessonToModule: async (courseId, moduleId, lessonData) => {
    try {
      const url = API_ENDPOINTS.TRAINING.COURSE_LESSONS(courseId, moduleId);

      const response = await post(url, lessonData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to add lesson'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to add lesson'
      };
    }
  },

  // Update lesson in module
  updateLessonInModule: async (courseId, moduleId, lessonId, lessonData) => {
    try {
      const response = await put(API_ENDPOINTS.TRAINING.COURSE_LESSON(courseId, moduleId, lessonId), lessonData);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update lesson'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update lesson'
      };
    }
  },

  // Delete lesson from module
  deleteLessonFromModule: async (courseId, moduleId, lessonId) => {
    try {
      const response = await del(API_ENDPOINTS.TRAINING.COURSE_LESSON(courseId, moduleId, lessonId));

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete lesson'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete lesson'
      };
    }
  },

  // ===== LEARNING PATHS APIs =====


  // Update learning path
  updateLearningPath: async (pathId, pathData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}`, pathData);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to update learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update learning path'
      };
    }
  },

  // Delete learning path
  deleteLearningPath: async (pathId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to delete learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete learning path'
      };
    }
  },

  // Enroll in learning path
  enrollInLearningPath: async (pathId, enrollmentData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}/enroll`, enrollmentData);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to enroll in learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to enroll in learning path'
      };
    }
  },

  // Get learning path progress
  getLearningPathProgress: async (pathId, queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}/progress?${queryString}` : `${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}/progress`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch learning path progress'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch learning path progress'
      };
    }
  },

  // ===== ATTENDANCE APIs =====

  // Get attendance records
  getAttendance: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.TRAINING.ATTENDANCE}?${queryString}` : API_ENDPOINTS.TRAINING.ATTENDANCE;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data.attendanceRecords || response.data,
          pagination: response.data.pagination,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch attendance records'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch attendance records'
      };
    }
  },

  // Mark attendance
  markAttendance: async (attendanceData) => {
    try {
      const response = await post(API_ENDPOINTS.TRAINING.ATTENDANCE, attendanceData);

      if (response.responseCode === 201) {
        return {
          success: true,
          responseCode: 201,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to mark attendance'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to mark attendance'
      };
    }
  },

  // Bulk mark attendance
  bulkMarkAttendance: async (bulkData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.ATTENDANCE}/bulk`, bulkData);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to bulk mark attendance'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to bulk mark attendance'
      };
    }
  },

  // Update attendance
  updateAttendance: async (attendanceId, attendanceData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.ATTENDANCE}/${attendanceId}`, attendanceData);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to update attendance'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update attendance'
      };
    }
  },

  // Delete attendance
  deleteAttendance: async (attendanceId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.ATTENDANCE}/${attendanceId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to delete attendance'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete attendance'
      };
    }
  },

  // Import attendance
  importAttendance: async (importData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.ATTENDANCE}/import`, importData);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to import attendance'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to import attendance'
      };
    }
  },

  // Export attendance
  exportAttendance: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.TRAINING.ATTENDANCE}/export?${queryString}` : `${API_ENDPOINTS.TRAINING.ATTENDANCE}/export`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to export attendance'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export attendance'
      };
    }
  },

  // ===== FEEDBACK APIs =====

  // Get feedback templates
  getFeedbackTemplates: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.TRAINING.FEEDBACK}/templates?${queryString}` : `${API_ENDPOINTS.TRAINING.FEEDBACK}/templates`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data.templates || response.data,
          pagination: response.data.pagination,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch feedback templates'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch feedback templates'
      };
    }
  },

  // Create feedback template
  createFeedbackTemplate: async (templateData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.FEEDBACK}/templates`, templateData);

      if (response.responseCode === 201) {
        return {
          success: true,
          responseCode: 201,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to create feedback template'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create feedback template'
      };
    }
  },

  // Update feedback template
  updateFeedbackTemplate: async (templateId, templateData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.FEEDBACK}/templates/${templateId}`, templateData);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to update feedback template'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update feedback template'
      };
    }
  },

  // Delete feedback template
  deleteFeedbackTemplate: async (templateId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.FEEDBACK}/templates/${templateId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to delete feedback template'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete feedback template'
      };
    }
  },

  // Get feedback submissions
  getFeedbackSubmissions: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.TRAINING.FEEDBACK}/submissions?${queryString}` : `${API_ENDPOINTS.TRAINING.FEEDBACK}/submissions`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data.submissions || response.data,
          pagination: response.data.pagination,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch feedback submissions'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch feedback submissions'
      };
    }
  },

  // Submit feedback
  submitFeedback: async (feedbackData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.FEEDBACK}/submissions`, feedbackData);

      if (response.responseCode === 201) {
        return {
          success: true,
          responseCode: 201,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to submit feedback'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to submit feedback'
      };
    }
  },

  // Get feedback analytics
  getFeedbackAnalytics: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.TRAINING.FEEDBACK}/analytics?${queryString}` : `${API_ENDPOINTS.TRAINING.FEEDBACK}/analytics`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch feedback analytics'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch feedback analytics'
      };
    }
  },

  // ===== ATTENDANCE APIs =====
  getAttendance: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();
      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.status && queryParams.status !== 'all') queryString.append('status', queryParams.status);
      if (queryParams.sessionId && queryParams.sessionId !== 'all') queryString.append('sessionId', queryParams.sessionId);
      if (queryParams.date) queryString.append('date', queryParams.date);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.ATTENDANCE}?${queryString.toString()}` : API_ENDPOINTS.TRAINING.ATTENDANCE;
      const response = await get(url);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch attendance' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch attendance' };
    }
  },

  // ===== ANALYTICS & REPORTS APIs =====
  getTrainingAnalytics: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();
      if (queryParams.period) queryString.append('period', queryParams.period);
      if (queryParams.department && queryParams.department !== 'all') queryString.append('department', queryParams.department);
      if (queryParams.courseId && queryParams.courseId !== 'all') queryString.append('courseId', queryParams.courseId);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.ANALYTICS}?${queryString.toString()}` : API_ENDPOINTS.TRAINING.ANALYTICS;
      const response = await get(url);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch training analytics' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch training analytics' };
    }
  },

  getTrainingReports: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();
      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.reportType && queryParams.reportType !== 'all') queryString.append('reportType', queryParams.reportType);
      if (queryParams.period) queryString.append('period', queryParams.period);
      if (queryParams.department && queryParams.department !== 'all') queryString.append('department', queryParams.department);
      if (queryParams.courseId && queryParams.courseId !== 'all') queryString.append('courseId', queryParams.courseId);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.REPORTS}?${queryString.toString()}` : API_ENDPOINTS.TRAINING.REPORTS;
      const response = await get(url);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch training reports' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch training reports' };
    }
  },

  // ===== COMPLIANCE APIs =====
  getComplianceData: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();
      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.status && queryParams.status !== 'all') queryString.append('status', queryParams.status);
      if (queryParams.category && queryParams.category !== 'all') queryString.append('category', queryParams.category);
      if (queryParams.department && queryParams.department !== 'all') queryString.append('department', queryParams.department);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.COMPLIANCE}?${queryString.toString()}` : API_ENDPOINTS.TRAINING.COMPLIANCE;
      const response = await get(url);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch compliance data' };
      }
    } catch (error) {
      // Return failure immediately to trigger fallback to mock data
      return { success: false, error: error.message || 'Failed to fetch compliance data' };
    }
  },

  getExpiringCertifications: async () => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.COMPLIANCE}/expiring`);
      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch expiring certifications' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch expiring certifications' };
    }
  },

  getExpiredCertifications: async () => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.COMPLIANCE}/expired`);
      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch expired certifications' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch expired certifications' };
    }
  },

  // Get compliance analytics
  getComplianceAnalytics: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();
      if (queryParams.department) queryString.append('department', queryParams.department);
      if (queryParams.period) queryString.append('period', queryParams.period);
      if (queryParams.startDate) queryString.append('startDate', queryParams.startDate);
      if (queryParams.endDate) queryString.append('endDate', queryParams.endDate);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.COMPLIANCE}/analytics?${queryString.toString()}` : `${API_ENDPOINTS.TRAINING.COMPLIANCE}/analytics`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch compliance analytics' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch compliance analytics' };
    }
  },

  // Send compliance reminders
  sendComplianceReminders: async (certificationIds) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.COMPLIANCE}/reminders`, { certificationIds });
      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to send compliance reminders' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to send compliance reminders' };
    }
  },

  // Renew certification
  renewCertification: async (certificationId, renewalData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.COMPLIANCE}/${certificationId}/renew`, renewalData);
      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to renew certification' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to renew certification' };
    }
  },

  // Block/Unblock access
  toggleAccess: async (certificationId, blocked) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.COMPLIANCE}/${certificationId}/access`, { blocked });
      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to toggle access' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to toggle access' };
    }
  },

  // Export compliance data
  exportComplianceData: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();
      if (queryParams.format) queryString.append('format', queryParams.format);
      if (queryParams.department) queryString.append('department', queryParams.department);
      if (queryParams.status) queryString.append('status', queryParams.status);
      if (queryParams.startDate) queryString.append('startDate', queryParams.startDate);
      if (queryParams.endDate) queryString.append('endDate', queryParams.endDate);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.COMPLIANCE}/export?${queryString.toString()}` : `${API_ENDPOINTS.TRAINING.COMPLIANCE}/export`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to export compliance data' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to export compliance data' };
    }
  },

  // Bulk actions for compliance
  bulkComplianceAction: async (action, certificationIds, actionData = {}) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.COMPLIANCE}/bulk-action`, {
        action,
        certificationIds,
        ...actionData
      });
      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to perform bulk action' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to perform bulk action' };
    }
  },

  // ===== SETTINGS APIs =====
  getTrainingSettings: async () => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.SETTINGS}`);
      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch training settings' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch training settings' };
    }
  },

  getTrainingPolicies: async () => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.SETTINGS}/policies`);
      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch training policies' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch training policies' };
    }
  },

  // ===== MODULES & LESSONS APIs =====
  getModules: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();
      if (queryParams.courseId) queryString.append('courseId', queryParams.courseId);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.MODULES}?${queryString.toString()}` : API_ENDPOINTS.TRAINING.MODULES;
      const response = await get(url);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch modules' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch modules' };
    }
  },

  createModule: async (moduleData) => {
    try {
      const response = await post(API_ENDPOINTS.TRAINING.MODULES, moduleData);

      if (response.responseCode === 201) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to create module' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create module' };
    }
  },

  updateModule: async (moduleId, moduleData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}`, moduleData);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to update module' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update module' };
    }
  },

  deleteModule: async (moduleId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}`);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to delete module' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to delete module' };
    }
  },

  getLessons: async (moduleId) => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}/lessons`);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch lessons' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch lessons' };
    }
  },

  createLesson: async (moduleId, lessonData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}/lessons`, lessonData);

      if (response.responseCode === 201) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to create lesson' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create lesson' };
    }
  },

  updateLesson: async (moduleId, lessonId, lessonData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}/lessons/${lessonId}`, lessonData);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to update lesson' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update lesson' };
    }
  },

  deleteLesson: async (moduleId, lessonId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.MODULES}/${moduleId}/lessons/${lessonId}`);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to delete lesson' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to delete lesson' };
    }
  },

  // ===== QUIZ & QUESTION BANK APIs =====
  getQuestions: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams();
      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.type && queryParams.type !== 'all') queryString.append('type', queryParams.type);
      if (queryParams.difficulty && queryParams.difficulty !== 'all') queryString.append('difficulty', queryParams.difficulty);
      if (queryParams.subject && queryParams.subject !== 'all') queryString.append('subject', queryParams.subject);
      if (queryParams.tags) queryString.append('tags', queryParams.tags);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.QUIZZES}/questions?${queryString.toString()}` : `${API_ENDPOINTS.TRAINING.QUIZZES}/questions`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to fetch questions' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch questions' };
    }
  },

  createQuestion: async (questionData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.QUIZZES}/questions`, questionData);

      if (response.responseCode === 201) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to create question' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create question' };
    }
  },

  updateQuestion: async (questionId, questionData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.QUIZZES}/questions/${questionId}`, questionData);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to update question' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update question' };
    }
  },

  deleteQuestion: async (questionId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.QUIZZES}/questions/${questionId}`);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to delete question' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to delete question' };
    }
  },


  createQuiz: async (quizData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.QUIZZES}/quizzes`, quizData);

      if (response.responseCode === 201) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to create quiz' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create quiz' };
    }
  },

  updateQuiz: async (quizId, quizData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.QUIZZES}/quizzes/${quizId}`, quizData);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to update quiz' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update quiz' };
    }
  },

  deleteQuiz: async (quizId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.QUIZZES}/quizzes/${quizId}`);

      if (response.responseCode === 200) {
        return { success: true, data: response.data, message: response.responseMessage };
      } else {
        return { success: false, error: response.responseMessage || 'Failed to delete quiz' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to delete quiz' };
    }
  },

  // Upload training files
  uploadTrainingFile: async (file, type = 'document') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      // Get auth token
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        return {
          success: false,
          error: 'No authentication token found'
        };
      }

      // Use direct fetch for file uploads
      const response = await fetch(`${API_BASE_URL}/training/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          error: result.responseMessage || 'Failed to upload file'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to upload file'
      };
    }
  },

  // Delete training file from Cloudinary
  deleteTrainingFile: async (publicId) => {
    try {
      if (!publicId) {
        return {
          success: false,
          error: 'Public ID is required for file deletion'
        };
      }

      // Get auth token
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        return {
          success: false,
          error: 'No authentication token found'
        };
      }

      // Encode publicId to safely pass Cloudinary folder paths (e.g., hrms-training/videos/...)
      const encodedPublicId = encodeURIComponent(publicId);

      // Use direct fetch for file deletion
      const response = await fetch(`${API_BASE_URL}/training/upload/${encodedPublicId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        return {
          success: true,
          data: result.data,
          message: result.responseMessage
        };
      } else {
        return {
          success: false,
          error: result.responseMessage || 'Failed to delete file'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete file'
      };
    }
  },

  // ==================== LEARNING PATHS ====================

  // Get learning paths with advanced filtering
  getLearningPaths: async (queryParams = {}) => {
    try {
      // Build query string from parameters
      const queryString = new URLSearchParams();

      if (queryParams.search) queryString.append('search', queryParams.search);
      if (queryParams.category && queryParams.category !== 'all') queryString.append('category', queryParams.category);
      if (queryParams.level && queryParams.level !== 'all') queryString.append('level', queryParams.level);
      if (queryParams.difficulty && queryParams.difficulty !== 'all') queryString.append('difficulty', queryParams.difficulty);
      if (queryParams.status && queryParams.status !== 'all') queryString.append('status', queryParams.status);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);
      if (queryParams.sortBy) queryString.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) queryString.append('sortOrder', queryParams.sortOrder);
      if (queryParams.startDate) queryString.append('startDate', queryParams.startDate);
      if (queryParams.endDate) queryString.append('endDate', queryParams.endDate);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.LEARNING_PATHS}?${queryString.toString()}` : API_ENDPOINTS.TRAINING.LEARNING_PATHS;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          responseCode: 200,
          data: {
            learningPaths: response.data.learningPaths || [],
            pagination: response.data.pagination || {}
          },
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch learning paths'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch learning paths'
      };
    }
  },

  // Get learning path by ID
  getLearningPathById: async (pathId) => {
    try {
      const response = await get(`${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch learning path'
      };
    }
  },

  // Create learning path
  createLearningPath: async (learningPathData) => {
    try {
      const response = await post(API_ENDPOINTS.TRAINING.LEARNING_PATHS, learningPathData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create learning path'
      };
    }
  },

  // Update learning path
  updateLearningPath: async (pathId, learningPathData) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}`, learningPathData);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update learning path'
      };
    }
  },

  // Delete learning path
  deleteLearningPath: async (pathId) => {
    try {
      const response = await del(`${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete learning path'
      };
    }
  },

  // Publish learning path
  publishLearningPath: async (pathId) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}/publish`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to publish learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to publish learning path'
      };
    }
  },

  // Archive learning path
  archiveLearningPath: async (pathId) => {
    try {
      const response = await put(`${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}/archive`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to archive learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to archive learning path'
      };
    }
  },

  // Enroll in learning path
  enrollInLearningPath: async (pathId, enrollmentData) => {
    try {
      const response = await post(`${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}/enroll`, enrollmentData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to enroll in learning path'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to enroll in learning path'
      };
    }
  },

  // Get learning path enrollments
  getLearningPathEnrollments: async (pathId, queryParams = {}) => {
    try {
      // Build query string from parameters
      const queryString = new URLSearchParams();

      if (queryParams.status && queryParams.status !== 'all') queryString.append('status', queryParams.status);
      if (queryParams.page) queryString.append('page', queryParams.page);
      if (queryParams.limit) queryString.append('limit', queryParams.limit);

      const url = queryString.toString() ? `${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}/enrollments?${queryString.toString()}` : `${API_ENDPOINTS.TRAINING.LEARNING_PATHS}/${pathId}/enrollments`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch enrollments'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch enrollments'
      };
    }
  },

  // Get departments for filter options
  getDepartments: async (params = {}) => {
    try {
      // Build query string from parameters
      const queryString = new URLSearchParams();

      if (params.isActive !== undefined && params.isActive !== 'all') {
        queryString.append('isActive', params.isActive === true || params.isActive === 'true' ? 'true' : 'false');
      }
      if (params.search) queryString.append('search', params.search);
      if (params.paginated !== undefined) queryString.append('paginated', params.paginated);

      const url = queryString.toString() ? `${API_BASE_URL}/hr/departments?${queryString.toString()}` : `${API_BASE_URL}/hr/departments`;
      const response = await get(url);

      if (response.responseCode === 200) {
        // Handle both response formats:
        // - When paginated='false': response.data is an array directly
        // - When paginated='true': response.data.departments is the array
        let departments = [];
        if (Array.isArray(response.data)) {
          departments = response.data;
        } else if (response.data?.departments && Array.isArray(response.data.departments)) {
          departments = response.data.departments;
        }

        return {
          success: true,
          data: departments,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch departments'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch departments'
      };
    }
  },

  // Get locations for filter options
  getLocations: async () => {
    try {
      const response = await get(`${API_BASE_URL}/hr/locations`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data.locations || [],
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch locations'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch locations'
      };
    }
  },

  // Get certificates (for expiring certifications)
  getCertificates: async (queryParams = {}) => {
    try {
      // Use the dedicated expiring endpoint if expiring=true
      if (queryParams.expiring === true || queryParams.expiring === 'true') {
        const url = `${API_BASE_URL}/training-compliance/expiring`;
        const response = await get(url);

        if (response.responseCode === 200) {
          return {
            success: true,
            data: response.data || [],
            message: response.responseMessage
          };
        } else {
          return {
            success: false,
            responseCode: response.responseCode,
            error: response.responseMessage || 'Failed to fetch expiring certificates'
          };
        }
      }

      // For other requests, use the main compliance endpoint
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_BASE_URL}/training-compliance?${queryString}` : `${API_BASE_URL}/training-compliance`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data.complianceData || response.data || [],
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch certificates'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch certificates'
      };
    }
  },

  // Get assignments (for pending approvals)
  getAssignmentsForApproval: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_BASE_URL}/training-assignments?${queryString}` : `${API_BASE_URL}/training-assignments`;
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data.assignments || response.data || [],
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch assignments'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch assignments'
      };
    }
  },

};

export default trainingService;
